import React, { useState } from 'react';
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Skeleton,
  Paper,
  Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FlightIcon from '@mui/icons-material/Flight';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import DirectionsRailwayIcon from '@mui/icons-material/DirectionsRailway';
import PlaceIcon from '@mui/icons-material/Place';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { formatDate } from '../../../../../utils/dateUtils';

// Define the Trip interface
export interface TripData {
  id: string;
  tripName: string;
  departureDate: string;
  returnDate?: string;
  destination?: string;
  tripDescription?: string;
  transportationType?: string;
  accommodationDetails?: string;
  cost?: number;
  currency?: string;
  maxParticipants?: number;
  status?: 'planned' | 'booking' | 'confirmed' | 'in-progress' | 'completed' | 'cancelled';
  imageUrl?: string;
  [key: string]: any; // Allow for additional properties
}

// Props interface
interface TripSelectionStepProps {
  trips: TripData[];
  selectedTrip: TripData | null;
  onTripSelect: (trip: TripData) => void;
  loading: boolean;
}

const TripSelectionStep: React.FC<TripSelectionStepProps> = ({
  trips,
  selectedTrip,
  onTripSelect,
  loading
}) => {
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Filter trips based on search query
  const filteredTrips = trips.filter(trip => {
    const query = searchQuery.toLowerCase();
    return (
      trip.tripName.toLowerCase().includes(query) ||
      (trip.destination || '').toLowerCase().includes(query) ||
      (trip.tripDescription || '').toLowerCase().includes(query) ||
      (trip.transportationType || '').toLowerCase().includes(query)
    );
  });

  // Get the transportation icon based on the type
  const getTransportIcon = (type?: string) => {
    if (!type) return <FlightIcon />;
    
    const lowercaseType = type.toLowerCase();
    if (lowercaseType.includes('flight') || lowercaseType.includes('plane') || lowercaseType.includes('air')) {
      return <FlightIcon />;
    } else if (lowercaseType.includes('car') || lowercaseType.includes('drive')) {
      return <DirectionsCarIcon />;
    } else if (lowercaseType.includes('train') || lowercaseType.includes('rail')) {
      return <DirectionsRailwayIcon />;
    } else if (lowercaseType.includes('boat') || lowercaseType.includes('cruise') || lowercaseType.includes('ship')) {
      return <DirectionsBoatIcon />;
    }
    
    return <FlightIcon />;
  };

  // Get the status color
  const getStatusColor = (status?: string) => {
    if (!status) return 'default';
    
    switch (status) {
      case 'planned':
        return 'primary';
      case 'booking':
        return 'warning';
      case 'confirmed':
        return 'success';
      case 'in-progress':
        return 'info';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  // Render loading skeletons
  const renderSkeletons = () => {
    return Array(3).fill(0).map((_, index) => (
      <Grid item xs={12} sm={6} md={4} key={`skeleton-${index}`}>
        <Card>
          <Skeleton variant="rectangular" height={140} />
          <CardContent>
            <Skeleton variant="text" height={30} />
            <Skeleton variant="text" height={20} width="60%" />
            <Skeleton variant="text" height={20} width="75%" />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              <Skeleton variant="circular" width={40} height={40} />
              <Skeleton variant="rectangular" width={80} height={30} />
            </Box>
          </CardContent>
        </Card>
      </Grid>
    ));
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Select a Trip
      </Typography>
      
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Search trips by name, destination, or description..."
        margin="normal"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3 }}
      />
      
      {loading ? (
        <Grid container spacing={3}>
          {renderSkeletons()}
        </Grid>
      ) : filteredTrips.length > 0 ? (
        <Grid container spacing={3}>
          {filteredTrips.map((trip) => (
            <Grid item xs={12} sm={6} md={4} key={trip.id}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  border: selectedTrip?.id === trip.id ? '2px solid #3f51b5' : 'none'
                }}
              >
                <CardActionArea onClick={() => onTripSelect(trip)}>
                  <CardMedia
                    component="img"
                    height="180"
                    image={trip.imageUrl || `https://source.unsplash.com/featured/?travel,${encodeURIComponent(trip.destination || 'trip')}`}
                    alt={trip.tripName}
                    sx={{ 
                      objectFit: 'cover',
                      objectPosition: 'center',
                      boxShadow: 'inset 0 -40px 40px -30px rgba(0,0,0,0.5)',
                      filter: 'brightness(1.05) contrast(1.05)'
                    }}
                  />
                  <Chip 
                    label={trip.status || 'planned'}
                    color={getStatusColor(trip.status) as any}
                    size="small"
                    sx={{ 
                      position: 'absolute', 
                      top: 10, 
                      right: 10,
                      textTransform: 'capitalize',
                      fontWeight: 'bold',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}
                  />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" component="div" gutterBottom>
                      {trip.tripName}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PlaceIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {trip.destination || 'Destination TBD'}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CalendarTodayIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(trip.departureDate)}
                        {trip.returnDate ? ` - ${formatDate(trip.returnDate)}` : ''}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {getTransportIcon(trip.transportationType)}
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                        {trip.transportationType || 'Transportation TBD'}
                      </Typography>
                    </Box>
                    
                    {trip.cost && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <AttachMoneyIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {trip.cost} {trip.currency || 'USD'}
                        </Typography>
                      </Box>
                    )}
                    
                    {trip.tripDescription && (
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ 
                          mt: 1,
                          display: '-webkit-box',
                          overflow: 'hidden',
                          WebkitBoxOrient: 'vertical',
                          WebkitLineClamp: 2,
                        }}
                      >
                        {trip.tripDescription}
                      </Typography>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper 
          sx={{ 
            p: 3, 
            textAlign: 'center', 
            bgcolor: 'background.default'
          }}
        >
          {searchQuery ? (
            <Typography variant="body1">
              No trips match your search query. Try different keywords.
            </Typography>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              No trips are available. Please create a trip first or contact your administrator.
            </Alert>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default TripSelectionStep;
